import { DependencyList, useEffect, useState } from "react";

export type AsyncState<T> =
    | { status: "loading"; data?: undefined; error?: undefined }
    | { status: "success"; data: T; error?: undefined }
    | { status: "error"; data?: undefined; error: unknown };

/** Runs an API call when its dependencies change, ignoring the results of superseded calls. */
export function useAsync<T>(load: () => Promise<T>, dependencies: DependencyList): AsyncState<T> {
    const [settled, setSettled] = useState<{ dependencies: DependencyList; state: AsyncState<T> } | undefined>();
    useEffect(() => {
        let current = true;
        load().then(
            (data) => {
                if (current) {
                    setSettled({ dependencies, state: { status: "success", data } });
                }
            },
            (error: unknown) => {
                if (current) {
                    setSettled({ dependencies, state: { status: "error", error } });
                }
            },
        );
        return () => {
            current = false;
        };
        // The caller lists the dependencies of `load`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencies);
    // Loading until the call for the current dependencies settles.
    return settled !== undefined && sameDependencies(settled.dependencies, dependencies)
        ? settled.state
        : { status: "loading" };
}

function sameDependencies(a: DependencyList, b: DependencyList): boolean {
    return a.length === b.length && a.every((value, index) => Object.is(value, b[index]));
}
